import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { LoadingButton } from '@mui/lab';
import { alpha, Box, Button, Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toTitleCase } from 'v2/utils/strings';
import {
  getSelectedReasonDefinitions,
  trackCreateNewReasonEvents,
} from './helpers';
import { useGetReasonDefinitions, useGetReasonNameForRCs } from './hooks';
import { ReasonDescriptionInput } from './ReasonDescriptionInput';

export const EditReasonDescriptionPromptInput = () => {
  const abortController = useRef(new AbortController());

  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);
  const description = useTaxonomyStore(
    ({ createReasonV3State: { description } }) => description,
  );

  const [reasonDescription, setReasonDescription] = useState(description);

  const [getReasonDefinitions, { loading: isFetchingReasonDefinitions }] =
    useGetReasonDefinitions({
      abortSignal: abortController.current.signal,
    });

  const [getReasonNameForRC, { isLoading: isGeneratingReasonNameLoading }] =
    useGetReasonNameForRCs({
      abortSignal: abortController.current.signal,
      onCompleted: ({ displayName, category }) => {
        dispatch({
          type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
          payload: {
            reason: {
              name: toTitleCase(displayName),
              category: mapEdgeCategoryToCategory(category),
            },
          },
        });
      },
    });

  const closeEditPromptMode = () => {
    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        visual: {
          isEditPromptMode: false,
        },
      },
    });
  };

  const onUpdatePrompt = async () => {
    if (!reasonDescription) {
      return;
    }

    try {
      trackCreateNewReasonEvents({
        eventName: 'Create New Reason - Description Prompt Edited',
        description,
        newDescription: reasonDescription,
      });
    } catch {}

    const { definitions, error } = await getReasonDefinitions(
      reasonDescription,
    );
    // Don't move to next step if API returns an error
    // The error notification is triggered from the hook callback
    if (error) {
      return;
    }

    await getReasonNameForRC(getSelectedReasonDefinitions(definitions));

    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        description: reasonDescription,
      },
    });

    closeEditPromptMode();
  };

  const isLoading =
    isFetchingReasonDefinitions || isGeneratingReasonNameLoading;

  useEffect(() => {
    const abortAPICalls = () => {
      abortController.current.abort();
      // Reset the signal
      abortController.current = new AbortController();
    };

    return () => abortAPICalls();
  }, []);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      py={1.25}
      px={3}
      sx={{ background: (theme) => alpha(theme.palette.text.primary, 0.04) }}
    >
      <Box flex={6}>
        <ReasonDescriptionInput
          description={reasonDescription}
          onDescriptionChange={setReasonDescription}
          showAdditionalDetails={false}
          textFieldProps={{
            rows: 3,
            inputProps: {
              style: { fontSize: 14 },
            },
          }}
        />
      </Box>
      <Stack flex={1} spacing={1} pt={2}>
        <LoadingButton
          variant="contained"
          onClick={onUpdatePrompt}
          loading={isLoading}
        >
          Update Prompt
        </LoadingButton>
        <Button variant="outlined" onClick={closeEditPromptMode}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
